import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';

const DatabaseTest: React.FC = () => {
  const { user } = useAuth();
  const [result, setResult] = useState<string>('');

  const createTestScan = async () => {
    try {
      if (!user) {
        setResult('No user logged in');
        return;
      }

      const { data, error } = await supabase
        .from('scan_history')
        .insert({
          user_id: user.id,
          title: 'Test Scan',
          description: 'Test scan for debugging',
          value: 'Test value',
          ai_explanation: 'This is a test explanation',
          api_provider: 'test',
          is_favorite: false,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        setResult(`Error: ${JSON.stringify(error, null, 2)}`);
      } else {
        setResult(`Success: ${JSON.stringify(data, null, 2)}`);
      }
    } catch (error) {
      setResult(`Exception: ${error}`);
    }
  };

  const queryMyScans = async () => {
    try {
      if (!user) {
        setResult('No user logged in');
        return;
      }

      const { data, error } = await supabase
        .from('scan_history')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        setResult(`Error: ${JSON.stringify(error, null, 2)}`);
      } else {
        setResult(`Query result: ${JSON.stringify(data, null, 2)}`);
      }
    } catch (error) {
      setResult(`Exception: ${error}`);
    }
  };

  const testAnalyticsQuery = async () => {
    try {
      if (!user) {
        setResult('No user logged in');
        return;
      }

      const now = new Date();
      const daysAgo = 30; // Same as analytics page default
      const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
      
      console.log('Testing analytics query with:', {
        userId: user.id,
        startDate: startDate.toISOString(),
        now: now.toISOString()
      });

      // Test the exact query from analytics page
      const { data: scansData, error: scansError } = await supabase
        .from('scan_history')
        .select('*, is_favorite')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (scansError) {
        setResult(`Scans Query Error: ${JSON.stringify(scansError, null, 2)}`);
        return;
      }

      // Test all-time query
      const { data: allScansData, error: allScansError } = await supabase
        .from('scan_history')
        .select('*, is_favorite')
        .eq('user_id', user.id);

      if (allScansError) {
        setResult(`All Scans Query Error: ${JSON.stringify(allScansError, null, 2)}`);
        return;
      }

      const totalScans = allScansData?.length || 0;
      const savedItems = allScansData?.filter(scan => scan.is_favorite === true).length || 0;
      const recentScans = scansData?.length || 0;

      setResult(`Analytics Query Test Results:
Total Scans: ${totalScans}
Saved Items: ${savedItems}
Recent Scans (last ${daysAgo} days): ${recentScans}

All Scans Data: ${JSON.stringify(allScansData, null, 2)}

Recent Scans Data: ${JSON.stringify(scansData, null, 2)}`);
    } catch (error) {
      setResult(`Exception: ${error}`);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (!user) {
        setResult('No user logged in');
        return;
      }

      // Get the first scan
      const { data: scans, error: queryError } = await supabase
        .from('scan_history')
        .select('*')
        .eq('user_id', user.id)
        .limit(1);

      if (queryError || !scans || scans.length === 0) {
        setResult('No scans found to toggle favorite');
        return;
      }

      const scan = scans[0];
      const newFavoriteStatus = !scan.is_favorite;

      const { data, error } = await supabase
        .from('scan_history')
        .update({ is_favorite: newFavoriteStatus })
        .eq('id', scan.id)
        .select()
        .single();

      if (error) {
        setResult(`Error: ${JSON.stringify(error, null, 2)}`);
      } else {
        setResult(`Favorite toggled: ${JSON.stringify(data, null, 2)}`);
      }
    } catch (error) {
      setResult(`Exception: ${error}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-900 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Database Test Page
      </h1>
      
      <div className="space-x-4 mb-6">
        <button
          onClick={createTestScan}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Create Test Scan
        </button>
        
        <button
          onClick={queryMyScans}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Query My Scans
        </button>
        
        <button
          onClick={testAnalyticsQuery}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Test Analytics Query
        </button>
        
        <button
          onClick={toggleFavorite}
          className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
        >
          Toggle Favorite on First Scan
        </button>
      </div>

      {/* Results Display */}
      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Result:
        </h3>
        <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words overflow-x-auto max-h-96 overflow-y-auto">
          {result}
        </pre>
      </div>
    </div>
  );
};

export default DatabaseTest;
